import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ClipboardList,
  Map,
  FileCheck,
  Truck,
  PackageCheck,
  Navigation,
  ArrowUpRight,
  X,
  Check,
  type LucideIcon,
} from 'lucide-react'
import { Section } from '../ui/Section'
import { Container } from '../ui/Container'
import { SectionTitle } from '../ui/SectionTitle'

type StepKey = 'needs' | 'planning' | 'customs' | 'vehicle' | 'pickup' | 'delivery'

interface StepDef {
  key: StepKey
  number: string
  icon: LucideIcon
  image: string
}

const steps: StepDef[] = [
  { key: 'needs', number: '01', icon: ClipboardList, image: '/images/method-needs.jpg' },
  { key: 'planning', number: '02', icon: Map, image: '/images/service-logistics.jpg' },
  { key: 'customs', number: '03', icon: FileCheck, image: '/images/service-intermodal.jpg' },
  { key: 'vehicle', number: '04', icon: Truck, image: '/images/company-fleet.jpg' },
  { key: 'pickup', number: '05', icon: PackageCheck, image: '/images/method-pickup-office.jpg' },
  { key: 'delivery', number: '06', icon: Navigation, image: '/images/chiggiato-logo-truck.jpg' },
]

export function MethodSteps() {
  const { t } = useTranslation('common')
  const [activeKey, setActiveKey] = useState<StepKey | null>(null)

  const active = activeKey ? steps.find((s) => s.key === activeKey) ?? null : null

  useEffect(() => {
    if (!activeKey) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveKey(null)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [activeKey])

  const features = active
    ? (t(`home.method.steps.${active.key}.features`, { returnObjects: true }) as string[])
    : []

  return (
    <Section variant="light">
      <Container>
        <SectionTitle
          title={t('home.method.title')}
          subtitle={t('home.method.subtitle')}
          centered
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, index) => {
            const Icon = step.icon
            const title = t(`home.method.steps.${step.key}.title`)
            return (
              <motion.button
                key={step.key}
                type="button"
                onClick={() => setActiveKey(step.key)}
                aria-label={t('home.method.aria.openDetail', { title })}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -6 }}
                className="group relative flex flex-col overflow-hidden rounded-lg bg-white text-left shadow-sm transition-shadow duration-normal hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-lg bg-brand-dark/5">
                  <img
                    src={step.image}
                    alt={title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 to-transparent" />
                  <div className="absolute left-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-white/95 text-brand-red shadow-md transition-all duration-normal group-hover:bg-brand-red group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-2 select-none text-6xl font-bold text-white/80 drop-shadow"
                  >
                    {step.number}
                  </span>
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand-red transition-transform duration-normal group-hover:scale-x-100" />

                <div className="relative flex flex-1 flex-col p-6">
                  <h3 className="mb-2 text-lg font-semibold text-brand-dark">
                    {title}
                  </h3>

                  <p className="text-sm leading-relaxed text-brand-text/80">
                    {t(`home.method.steps.${step.key}.description`)}
                  </p>

                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-red opacity-0 transition-all duration-normal group-hover:translate-x-1 group-hover:opacity-100">
                    {t('home.method.labels.discover')}
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.button>
            )
          })}
        </div>
      </Container>

      <AnimatePresence>
        {active && (
          <motion.div
            key="method-modal"
            className="fixed inset-0 z-modal flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="method-modal-title"
            onClick={() => setActiveKey(null)}
          >
            <div className="absolute inset-0 bg-brand-dark/70 backdrop-blur-sm" />

            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl"
            >
              <div className="flex items-start justify-between gap-4 bg-brand-red px-8 py-6 text-white">
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold opacity-80">{active.number}</span>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-white/15">
                    <active.icon className="h-6 w-6" />
                  </div>
                  <h3 id="method-modal-title" className="text-xl font-semibold md:text-2xl">
                    {t(`home.method.steps.${active.key}.title`)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveKey(null)}
                  aria-label={t('home.method.aria.closeDetail')}
                  className="-mr-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6 px-8 py-7">
                <p className="text-base leading-relaxed text-brand-text">
                  {t(`home.method.steps.${active.key}.detail`)}
                </p>

                {Array.isArray(features) && features.length > 0 && (
                  <div>
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-dark">
                      {t('home.method.labels.keyPoints')}
                    </h4>
                    <ul className="space-y-2">
                      {features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3 text-sm text-brand-text">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  )
}
